import React from "react";
import { StyleSheet, View, Text, Pressable, Platform, ScrollView } from "react-native";

import { useTheme } from "@/hooks/useTheme";

export type ErrorFallbackProps = {
  error: Error;
  resetError: () => void;
};

export function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const { theme } = useTheme();
  const updateRequired = Platform.OS === 'web' && /Loading module|ChunkLoadError|dynamically imported module/i.test(error?.message ?? '');
  // The boundary captures the Error upstream for monitoring. It is deliberately
  // NOT a prop here: nothing can render or copy its message/stack to a customer.

  const handleRestart = () => {
    if (Platform.OS === "web") {
      window.location.reload();
    } else {
      resetError();
    }
  };

  const styles = dsStyles(theme);

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.content}>
          <Text style={styles.title}>{updateRequired ? 'Load the latest Fahali screen' : 'Fahali needs to restart'}</Text>

          <Text style={styles.message}>
            {updateRequired ? 'This screen could not be downloaded. Reload to get the current version and try again. Your saved holdings are unchanged.' : 'Your data was not changed. Restart the app to continue.'}
          </Text>

          <View style={styles.row}>
            <Pressable
              onPress={handleRestart}
              style={({ pressed }) => [styles.button, { opacity: pressed ? 0.8 : 1 }]}
              accessibilityRole="button"
              accessibilityLabel={updateRequired ? 'Load latest version' : 'Restart Fahali'}
            >
              <Text style={styles.buttonText}>{updateRequired ? 'Load latest version' : 'Restart Fahali'}</Text>
            </Pressable>

          </View>
        </View>
      </ScrollView>
    </View>
  );
}

const dsStyles = (theme: any) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.backgroundRoot,
  },
  scroll: {
    flexGrow: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
  },
  content: {
    alignItems: "stretch",
    gap: 16,
    width: "100%",
    maxWidth: 720,
  },
  title: {
    color: theme.textPrimary,
    fontSize: 24,
    fontWeight: "700",
    textAlign: "center",
  },
  message: {
    color: theme.textTertiary,
    fontSize: 16,
    textAlign: "center",
    lineHeight: 24,
  },
  row: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 12,
    marginTop: 8,
    flexWrap: "wrap",
  },
  button: {
    backgroundColor: theme.riskGreen,
    paddingVertical: 14,
    paddingHorizontal: 32,
    borderRadius: 8,
    minWidth: 200,
  },
  buttonText: {
    color: theme.inkOnGold,
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
});
